import {
  FilterSectionData,
  FiltersSectionProps as BaseFiltersSectionProps,
} from "@shared/features/filters/types";
import React, { FC } from "react";
import styled from "styled-components";
import Title from "common/Typography/Title";
import { useFilter } from "@shared/features/filters/useFilter";
import SliderFiltersChips from "common/Filters/FiltersChips/SliderFiltersChips";
import MultiSelectFiltersSection from "common/Filters/MultiSelectFiltersSection";
import TimeRangeFiltersSection from "common/Filters/common/TimeRangeFiltersSection";
import BooleanFiltersSection from "common/Filters/FiltersColumn/BooleanFiltersSection";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import {
  FILTER_TYPES_DATA,
  FILTERS_HELPER_TOOLTIP_DATA,
} from "@shared/features/filters/constants";
import {
  AnalyticsPageName,
  FilterSectionType,
  LanguageNameSpaces,
} from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import { SMALL_MOBILE } from "common/constants";
import TagsFiltersSection from "common/Filters/FiltersColumn/TagsFiltersSection";
import PriceRangeFilterChips from "common/Filters/FiltersChips/PriceRangeFilterChips";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface FiltersSectionProps extends BaseFiltersSectionProps {
  isSingleRowView?: boolean;
}

const FiltersSection = ({
  filterType,
  filtersSelectors,
  filtersActions,
  isSingleRowView,
  filterTypeToTitle,
}: FiltersSectionProps) => {
  const isMobile = useMedia([`(max-width: ${SMALL_MOBILE})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const filterSectionData = useFilter({
    filterType,
    filtersActions,
    filtersSelectors,
    isMobile,
  });

  const FilterComponent =
    FILTER_SECTION_TYPE_TO_COMPONENT[filterSectionData.filterSectionType];
  if (!FilterComponent || filterSectionData?.shouldHide) return null;

  const filterTypeTooltipData = FILTERS_HELPER_TOOLTIP_DATA[filterType];
  const helperTooltipData = filterTypeTooltipData
    ? {
        ...filterTypeTooltipData,
        description: t(filterTypeTooltipData?.description),
      }
    : undefined;

  return (
    <div id={`filter-${FILTER_TYPES_DATA[filterType].queryKey}`}>
      {!isSingleRowView && (
        <FilterTitle>
          {filterTypeToTitle?.[filterType]
            ? t(filterTypeToTitle?.[filterType])
            : filterSectionData.title}
          {helperTooltipData && (
            <HelperTooltip
              {...helperTooltipData}
              analyticsPageName={AnalyticsPageName.FILTERS}
              placement="bottomRight"
            />
          )}
        </FilterTitle>
      )}
      <FilterComponent
        {...filterSectionData}
        isSingleRowView={isSingleRowView}
        isChipsView={true}
      />
    </div>
  );
};

export default FiltersSection;

const FILTER_SECTION_TYPE_TO_COMPONENT: {
  [key in FilterSectionType]?: FC<FilterSectionData<any>>;
} = {
  [FilterSectionType.SLIDER]: SliderFiltersChips,
  [FilterSectionType.CHECKBOX]: MultiSelectFiltersSection,
  [FilterSectionType.MULTI_SELECT]: MultiSelectFiltersSection,
  [FilterSectionType.RADIO]: MultiSelectFiltersSection,
  [FilterSectionType.TIME_RANGE]: TimeRangeFiltersSection,
  [FilterSectionType.BOOLEAN]: BooleanFiltersSection,
  [FilterSectionType.TAGS]: TagsFiltersSection,
  [FilterSectionType.PRICE_RANGE]: PriceRangeFilterChips,
};
const FilterTitle = styled(Title)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  display: flex;
  align-items: center;
  height: 22px;
  margin-bottom: 4px;
`;
